import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Copy, Check, PlayFill, PauseFill, Clock } from "@gravity-ui/icons";
import apiService from "../../api/apiService"; // Import apiService
import { useAudioPlayer } from "../../contexts/AudioPlayerContext"; // Import useAudioPlayer
import Waveform from "./Waveform"; // Import the Waveform component
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react"
import { useAuth } from "../../contexts/AuthContext";
// Default static data (used as fallback)
const defaultTrackData = { 
  title: "Happy tunes",
  prompt: "[Prompt]", 
  image: "/logo@2x.png", // Default fallback image
  tags: [
    { key: "theme", value: "[Theme]" },
    { key: "mood", value: "[Mood]" },
    { key: "genre", value: "[Genre]" },
    { key: "instrument", value: "[Instrument]" }
  ],
  audioSrc: null
};


// Helper function to format duration
const formatDuration = (seconds) => {
  if (seconds == null || isNaN(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60); // Use Math.floor to avoid decimals
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const SharePageIntro = () => {
  const [copied, setCopied] = useState(false);
  const { id: shareId } = useParams(); 
  const [trackData, setTrackData] = useState(null);
  const [similarTracks, setSimilarTracks] = useState([]); // State for similar tracks
  const [loadingSimilar, setLoadingSimilar] = useState(false); // Loading state for similar tracks
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const posthog = usePostHog()
  const {isLoggedIn} = useAuth();
  // Get audio player context
  const {
    isPlaying,
    playAudio,
    pauseAudio,
    resumeAudio,
    audioSrc: currentAudioSrc,
    currentTime,
    duration,
    // Add setAiResponseId and setDiscoverId if needed for context updates
    // setAiResponseId, 
    // setDiscoverId,
  } = useAudioPlayer();

  // Determine the correct streaming URL for this shared track
  const streamingUrl = shareId 
    ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-shared-audio/${shareId}` 
    : null;

  // Determine if *this* shared track is currently playing
  const isThisTrackPlaying = isPlaying && currentAudioSrc === streamingUrl;

  // Fetch primary track data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      console.log(`[Frontend] Attempting to fetch data for shareId: ${shareId}`);
      try {
        if (shareId) {
          const response = await apiService.getSharedTrack(shareId); 
          console.log("[Frontend] Raw API Response:", JSON.stringify(response, null, 2));

          if (response.success && response.sharedTrack) {
            const backendData = response.sharedTrack;
            console.log("[Frontend] Extracted backendData:", JSON.stringify(backendData, null, 2));
            // Log the specific fields received
            console.log("[Frontend] Received cover_image_url:", backendData.cover_image_url);
            console.log("[Frontend] Received prompt (caption):", backendData.prompt);
            console.log("[Frontend] Received music_description:", backendData.music_description);
            console.log("[Frontend] Received audio_file_url:", backendData.audio_file_url);

            // --- Robust Tag Parsing --- 
            let parsedTags = [...defaultTrackData.tags]; // Start with default
            const descriptionString = backendData.music_description; // Get the description
            
            if (typeof descriptionString === 'string' && descriptionString.trim() !== '') {
                console.log(`[Frontend] Attempting to parse description: "${descriptionString}"`);
                try {
                    const descriptions = descriptionString.split(' . ').map(d => d ? d.trim() : ''); // Handle potential nulls from split
                    console.log("[Frontend] Split descriptions:", descriptions);
                    
                    if (descriptions.length >= 4) { // Ensure we have enough parts
                      parsedTags = [
                          { key: "theme", value: descriptions[0] || "[Theme]" },
                          { key: "mood", value: descriptions[1] || "[Mood]" },
                          { key: "genre", value: descriptions[2] || "[Genre]" },
                          { key: "instrument", value: descriptions[3] || "[Instrument]" }
                      ];
                      console.log("[Frontend] Successfully parsed tags:", parsedTags); 
                    } else {
                       console.warn("[Frontend] music_description format unexpected (fewer than 4 parts after split):", descriptionString);
                       console.log("[Frontend] Using default tags due to parsing issue.");
                    }
                } catch (parseError) {
                    console.error("[Frontend] Error during parsing music_description:", parseError);
                    console.log("[Frontend] Using default tags due to parsing error.");
                    Sentry.captureException(parseError)
                }            
              } else {
              console.log("[Frontend] music_description is missing, null, empty, or not a string. Using default tags.");
            }
            // --- End Tag Parsing --- 
            
            const fetchedTrackData = {
              title: backendData.song_title || defaultTrackData.title,
              prompt: backendData.prompt || defaultTrackData.prompt, // Use prompt (caption) from backend
              image: backendData.cover_image_url || defaultTrackData.image, // Use cover_image_url
              audioSrc: backendData.audio_file_url || null, 
              tags: parsedTags, // Use parsed tags
              waveformData: backendData.waveform || { waveform: null }, // Include waveform data for visualization
              music_duration: backendData.duration || 0, // Include duration for proper timing
            };
            console.log("[Frontend] Final trackData state prepared:", JSON.stringify(fetchedTrackData, null, 2));
            
            setTrackData(fetchedTrackData);
            // Now fetch similar tracks using the parsed tags
            fetchSimilarTracks(parsedTags);
          } else {
            console.error("[Frontend] API call successful but sharedTrack missing.", response);
            throw new Error(response.message || "Shared track data not found.");
          }
        } else {
          console.log("[Frontend] No share ID found in URL.");
          throw new Error("No share ID provided in the URL.");
        }
      } catch (fetchError) {
        console.error("[Frontend] Error during fetch or processing:", fetchError);
        Sentry.captureException(fecthError);
        setError(fetchError.message || "Failed to load track data.");
        setTrackData(defaultTrackData); // Fallback to default on error
        setSimilarTracks([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [shareId]); 

  // Function to fetch similar tracks
  const fetchSimilarTracks = async (tags) => {
    if (!tags || tags.length < 4) {
        console.log("[Frontend] Cannot fetch similar tracks: missing primary track tags.");
        return;
    }
    setLoadingSimilar(true);
    console.log("[Frontend] Fetching similar tracks with tags:", tags);
    try {
        const payload = {
            theme: tags.find(t => t.key === 'theme')?.value,
            mood: tags.find(t => t.key === 'mood')?.value,
            genre: tags.find(t => t.key === 'genre')?.value,
            instrument: tags.find(t => t.key === 'instrument')?.value,
            // excludeId: trackData?.id // If you have a discover ID for the main track
        };
        
        // Check if all tags are valid before making the API call
        if (payload.theme && payload.mood && payload.genre && payload.instrument) {
            const response = await apiService.getSimilarTracks(payload);
            console.log("[Frontend] Similar tracks response:", response);
            
            // --- Process Results (if successful) ---
            if (response.success && response.similarTracks) {
                const processedTracks = response.similarTracks.map(song => {
                    // Log the raw song object received from backend for debugging
                    console.log("[Frontend fetchSimilarTracks] Raw song object from backend:", song);
                    return {
                      id: song.id,
                      title: song.title,
                      description: `${song.tags_theme || ''} . ${song.tags_mood || ''} . ${song.tags_genre || ''} . ${song.tags_instrument || ''}`.replace(/ \. $/,''), 
                      duration: song.duration, 
                      image: song.image, 
                      audioSrc: song.audioSrc, 
                      fileName: song.fileName, // **** USE song.fileName ****
                      waveformData: song.waveformData,
                      isLiked: song.is_liked, 
                    };
                });
                console.log("[Frontend fetchSimilarTracks] Processed tracks with fileName mapping:", processedTracks);
                setSimilarTracks(processedTracks);
            } else {
                console.warn("[Frontend] Failed to fetch similar tracks or none found.", response.message);
                setSimilarTracks([]); 
            }
        } else {
            console.warn("[Frontend] One or more tags missing from payload, skipping similar tracks fetch.", payload);
             setSimilarTracks([]); // Set empty if tags are missing
        }
    } catch (error) {
      console.error("[Frontend] Error fetching similar tracks:", error);
      Sentry.captureException(error);
      setSimilarTracks([]); // Set empty on error
    } finally {
      setLoadingSimilar(false);
    }
  };

  const handleCopyPrompt = () => {
    posthog.capture("Click-CopyPrompt-ShareTrack",{
      is_user_logged_in: isLoggedIn ? true : false, 
  })
    if (trackData?.prompt) {
        navigator.clipboard.writeText(trackData.prompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }
  };
  // Add the play/pause handler
  const handlePlayPause = () => {
    posthog.capture("Click-PlaySharedTrack-ShareTrack", {
      is_user_logged_in: isLoggedIn ? true : false,
    })
    if (!streamingUrl || !trackData) return; // Need URL and track data

    console.log(`[SharePage] handlePlayPause triggered. Current state: isPlaying=${isPlaying}, currentAudioSrc=${currentAudioSrc}, streamingUrl=${streamingUrl}`);

    if (isThisTrackPlaying) {
      console.log("[SharePage] Pausing audio.");
      pauseAudio();
    } else if (currentAudioSrc === streamingUrl) {
      console.log("[SharePage] Resuming audio.");
      resumeAudio();
    } else {
      console.log(`[SharePage] Playing new audio. URL: ${streamingUrl} - Starting from beginning`);
      // Start playing this track
      playAudio(
        streamingUrl, // Use the dedicated streaming URL
        trackData.title, // Pass necessary metadata
        trackData.prompt, // Use prompt as description for player context
        trackData.image, // Album art
        trackData.music_duration // Duration if available
        // Pass null for discoverId and potentially aiResponseId if not applicable here
      );
      // If you need to clear other contexts: 
      // setDiscoverId(null);
      // setAiResponseId(null); 
    }
  };
  // Handler for playing similar tracks
  const handlePlaySimilarTrack = (track) => {
    posthog.capture("Click-PlayDiscoverTrack-ShareTrack", {is_user_logged_in: isLoggedIn ? true : false})
    // Log the track object being used
    console.log("[Frontend handlePlaySimilarTrack] Track object:", track);

    if (!track || !track.fileName) { // Check specifically for fileName
      console.error("[Frontend handlePlaySimilarTrack] Error: Track object is missing fileName property.", track);
      return;
    }

    // Construct the CORRECT audio source URL using the NEW endpoint and fileName
    const similarAudioSrc = `${process.env.REACT_APP_SERVER_ENDPOINT || process.env.FRONTEND_URL}/api/stream-similar-audio/${track.fileName}`; 
    console.log(`[Frontend handlePlaySimilarTrack] Playing similar track: ${track.title}, Constructed URL: ${similarAudioSrc}`);

    // --- Play/Pause/Resume Logic --- 
    if (isPlaying && currentAudioSrc === similarAudioSrc) {
      console.log(`[Frontend handlePlaySimilarTrack] Pausing currently playing track: ${track.title}`);
      pauseAudio();
    } else if (currentAudioSrc === similarAudioSrc) {
      console.log(`[Frontend handlePlaySimilarTrack] Resuming paused track: ${track.title}`);
      resumeAudio();
    } else {
      console.log(`[Frontend handlePlaySimilarTrack] Playing new track: ${track.title}, should start from beginning`);
      playAudio(
        similarAudioSrc, // Use the CORRECT source URL
        track.title,
        track.description,
        track.image,
        track.duration // Pass raw duration
      );
    }
  };

  // Log the value right before rendering
  console.log("[Frontend] Rendering with trackData.image:", trackData?.image);

  // Render loading state
  if (isLoading) {
    return <div className="p-8 text-center text-white w-full h-full">Loading shared track...</div>;
  }
  
  // Render error state
  if (error) {
    return <div className="p-8 text-center w-full h-full text-red-500">Error: {error}</div>;
  }

  // Render content when data is ready
  if (!trackData) { 
    // Removed similarTracks check if they aren't used
    return <div className="p-8 text-center text-white w-full h-full">No track data available.</div>;
  }

  // TODO: Implement audio playback logic using trackData.audioSrc
  // You might need to integrate with useAudioPlayer context or similar

  return (
    <div className="flex flex-col w-full h-full">
      {/* Main content area */}
      <div className="flex flex-col md:items-center items-start md:flex-row gap-6 p-5 bg-gradient-to-t from-[#100F10] to-[#2C2A2C]">
        {/* Album art section - Ensure the img tag is present and uses trackData.image */} 
        <div className="w-56 h-56 bg-[#222] rounded-xl overflow-hidden relative">
          {/* THIS IS THE IMAGE FOR THE ALBUM ART */}
          <img 
            src={trackData?.image || defaultTrackData.image} 
            alt="Album art" 
            className="w-full h-full object-cover"
            onError={(e) => {
              console.error(`[Frontend] onError triggered for image src: ${e.target.src}. Falling back to default.`); 
              e.target.onerror = null;
              e.target.src = defaultTrackData.image; 
            }}
          />
          {/* Play Button Overlay */}
          <div className="absolute inset-0 flex items-end bg-[rgba(0,0,0,0.4)] justify-start p-3">
            <button 
              onClick={handlePlayPause} // Use the new handler
              className="w-12 h-12 bg-white hover:bg-[#E0DFE1] rounded-full flex items-center justify-center disabled:opacity-50"
              disabled={!trackData?.audioSrc || isLoading} // Also disable while loading data
            >
              {/* Conditionally render Play or Pause icon */}
              {isThisTrackPlaying ? (
                <PauseFill className="w-5 h-5 text-[#100f10]" />
              ) : (
                <PlayFill className="w-5 h-5 text-[#100f10]" /> 
              )}
            </button>
          </div>
        </div>
        
        {/* Track info section - uses trackData.title, trackData.prompt, trackData.tags */}
        <div className="flex-1 w-full">
          <h1 className="text-white text-4xl font-normal mb-1">
            {trackData?.title || 'Loading...'} 
          </h1>
          
          <div className="flex items-start gap-3 mb-2 w-full">
            <span className="text-[#C5C3C6] w-full text-base font-extralight">
              {trackData?.prompt || 'Loading...'} 
            </span>
            <button 
              onClick={handleCopyPrompt}
              className={`flex justify-center gap-2 items-center text-white/50 hover:text-white ${!trackData?.prompt || trackData.prompt === defaultTrackData.prompt ? 'hidden' : ''}`}
              disabled={!trackData?.prompt}
            >
              {copied ? (
                <>
                  Copied
                  <Check className="h-4 w-4" />
                </>
              ) : (
                <>
                  Copy
                  <Copy className="h-4 w-4" />
                </>
              )}
            </button>
          </div>            {/* Removed waveform from header, keeping only the duration display */}
          <div className="flex items-center w-full mb-2">
            {/* <div className="ml-2 text-sm text-white/60">
              {formatDuration(isThisTrackPlaying ? currentTime : 0)} / {formatDuration(duration || trackData?.music_duration)}
            </div> */}
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {/* Use optional chaining and check tag value before rendering */}
            {trackData?.tags && trackData.tags.map((tag) => (
              tag.value && tag.value !== `[${tag.key}]` ? (
                <span 
                  key={tag.key} 
                  className="px-2 h-7 flex items-center justify-center rounded-full bg-white/10 text-sm font-extralight text-white/40"
                >
                  {tag.value}
                </span>
              ) : null // Don't render if value is missing or default placeholder
            ))}
          </div>
        </div>
      </div>
      
      {/* Similar tracks section */}
      <div className="flex-col">
        <div className="border-b border-[#333] p-4">
          <h2 className="text-base">Similar Tracks</h2>
        </div>
        {loadingSimilar ? (
          <div className="flex w-full p-2 text-center text-white/50">Loading similar tracks...</div>
        ) : similarTracks.length > 0 ? (
          <div className="flex-col gap-2 w-full p-2 scrollable-content h-full"
          style={{
            maxHeight: `calc(100vh - 410px)`
          }}
          >
            {similarTracks.map((track) => {
              // Construct the correct streaming URL for this similar track
              const similarAudioSrc = track.fileName
                ? `${process.env.REACT_APP_SERVER_ENDPOINT || process.env.FRONTEND_URL}/api/stream-similar-audio/${track.fileName}`
                : null; // Handle cases where fileName might be missing

              // Check if THIS similar track is playing using the correct URL
              const isCurrentSimilarPlaying = isPlaying && currentAudioSrc === similarAudioSrc;

              // Log the specific waveform data being passed
              console.log(`[SharePageIntro] Passing waveformData prop for track ${track.title}:`, track.waveformData?.waveform);
              return (
                <div 
                  key={track.id || track.title} 
                  className={`flex items-center gap-4 py-2 hover:bg-white/5 transition-colors rounded-xl group pl-2 pr-4`}                  style={
                    currentAudioSrc === similarAudioSrc // Highlight based on whether this is the current track, not just if it's playing
                      ? {
                          background:
                            "linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.72) 100%), linear-gradient(135deg, #FC10F2 0%, #3078E4 100%)",
                        }
                      : {}
                  }
                >
                  <div className="w-12 h-12 flex-shrink-0 bg-[#333] rounded-md overflow-hidden relative group">
                    <img 
                      src={track.image} 
                      alt={track.title} 
                      className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-60"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultTrackData.image; // Fallback image
                      }}
                    />
                     <button
                        onClick={() => handlePlaySimilarTrack(track)}
                        // Disable button if the URL couldn't be constructed
                        disabled={!similarAudioSrc}
                        className={`absolute inset-0 flex items-center justify-center text-white transition-opacity duration-500 opacity-0 group-hover:opacity-100 ${currentAudioSrc === similarAudioSrc ? 'opacity-100' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {isCurrentSimilarPlaying ? (
                            <PauseFill className="w-5 h-5" />
                        ) : (
                            <PlayFill className="w-5 h-5" />
                        )}
                    </button>
                  </div>
                  <div className="flex-col w-full min-w-40">
                    <div className="font-medium text-white truncate">{track.title}</div>
                    <div className="text-sm text-[#878188] truncate">{track.description}</div>
                  </div>

                  <div className="flex md:justify-center justify-end items-center text-[#878188] text-sm w-full text-right">
                    <Clock className="w-3.5 h-3.5 mr-2 opacity-70"/>
                    {formatDuration(track.duration)}
                  </div>
                  
                  {/* Waveform component - Use the correct similarAudioSrc */}
                  <div className="hidden md:flex items-center max-w-[360px]">                    <Waveform
                      waveform={track.waveformData?.waveform}
                      // Pass the constructed URL to the waveform
                      audioSrc={similarAudioSrc}
                      currentTime={currentAudioSrc === similarAudioSrc ? currentTime : 0}
                      duration={currentAudioSrc === similarAudioSrc ? duration : track.duration}
                      isPlaying={isCurrentSimilarPlaying}
                      isActive={currentAudioSrc === similarAudioSrc} // Keep active state based on if this track is selected, not just playing
                    />
                  </div>

                  
                </div>
              );
            })}
          </div>
        ) : (
           !isLoading && <div className="text-center text-gray-500">No similar tracks found.</div> // Show only if main loading is done
        )}
      </div>
    </div>
  );
};

export default SharePageIntro; 